import useStore from "../../../store"
import { useTranslation } from "react-i18next"
import { NeutralButton } from "../../common/Buttons"
import ActionConfirmDrawer from "../../common/ActionConfirmDrawer"
import { useState, useRef } from "react"
import { Textarea, Field, Box, Button, Text, CloseButton } from '@chakra-ui/react'
import TransferPageBody from "../TransferPage/TransferPageBody"
import { colors } from "../../../utilities/colors"
import { FaCalendarAlt } from "react-icons/fa"
import DatePicker from "react-datepicker"

export default function UnitAchRescheduleRequest({
  transfer, isOnPendingTransfersPage = false, setOptionsOpen
}) {
  const { t } = useTranslation()
  const { createTransferChangeRequest } = useStore()
  const [isOpen, setIsOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const minDate = new Date(today)
  const [scheduleDate, setScheduleDate] = useState(null)
  const maxDate = new Date(today)
  maxDate.setDate(today.getDate() + 10)
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [errors, setErrors] = useState(null)
  const fetchPendingTransfers = useStore((state) => state.fetchPendingTransfers)
  const currentTransferDate = new Date(transfer.attributes['actual-transfer-date'])
  currentTransferDate.setHours(0, 0, 0, 0)

  const handleClick = () => inputRef.current.click()

  const handleCancel = () => {
    setFile(null)
    setImagePreview(null)
    setNotes('')
    setScheduleDate(null)
    setIsOpen(false)
    setErrors(null)
  }

  const handleChange = (e) => {
    const selected = e.target.files[0]
    setFile(selected)

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }

    if (selected) {
      reader.readAsDataURL(selected)
    }
    setErrors((prev) => ({ ...prev, dofFile: null }))
  }

  const handleRemoveFile = () => {
    setFile(null)
    inputRef.current.value = null
  }

  const handleCreateAchRescheduleTicket = async () => {
    const newErrors = {}

    if (!scheduleDate) {
      newErrors.scheduleDate = t('Please select a reschedule date')
    } else if (scheduleDate > currentTransferDate && !file) {
      newErrors.dofFile = "Delay of Funding (DOF) is required"
    }

    if (!notes.trim()) newErrors.notes = t('Please provide a reason for this request')

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    const formData = new FormData()
    formData.append(
      'transfer_change_request[requested_date]',
      scheduleDate.toISOString().split('T')[0]
    )
    formData.append('transfer_change_request[description]', notes)
    if (file) formData.append('transfer_change_request[dof_file]', file)

    await createTransferChangeRequest(transfer.id, formData)
    handleCancel()

    if (isOnPendingTransfersPage) {
      setOptionsOpen(false)
      fetchPendingTransfers()
    }
  }

  return (
    <>
      {
        isOnPendingTransfersPage ?
          <Box
            onClick={() => setIsOpen(true)}
            cursor={'pointer'}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            fontSize={'xs'}
          >
            <FaCalendarAlt />
            {t('Reschedule Payment')}
          </Box>
          :
          <NeutralButton
            text={t('Request ACH Reschedule')}
            onClick={() => setIsOpen(true)}
          />
      }
      <ActionConfirmDrawer
        title={t('Request ACH Reschedule')}
        onConfirm={handleCreateAchRescheduleTicket}
        isOpen={isOpen}
        onClose={handleCancel}
        onCancel={handleCancel}
        size={{ base: 'full', md: 'lg' }}
        body={
          <>
            <Box w={'full'}>
              <TransferPageBody transferId={transfer.id} showAltData={false} />
            </Box>

            <Field.Root id="scheduleDate" mb={2}>
              <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="scheduleDate">
                {t('Preferred Reschedule Date')}
              </Field.Label>
              <DatePicker
                id="scheduleDate"
                selected={scheduleDate}
                onChange={(date) => {
                  setScheduleDate(date)
                  setErrors((prev) => ({
                    ...prev,
                    scheduleDate: null,
                    dofFile: date <= currentTransferDate ? null : prev?.dofFile
                  }))
                }}
                dateFormat="M/d/yy"
                placeholderText={t('Select a date')}
                className={`px-1 py-0 w-full bg-grey-900 border-2 focus:ring-0 rounded ${errors?.scheduleDate ? 'border-fpx-red focus:border-fpx-red' : 'border-grey-700 focus:border-grey-300'}`}
                showPopperArrow={false}
                minDate={minDate}
                maxDate={maxDate}
              />
              {errors?.scheduleDate &&
                <Field.HelperText color={colors.fpxRed}>
                  {errors.scheduleDate}
                </Field.HelperText>
              }
              <Field.HelperText fontSize="xs" color="gray.400">
                {t('Transfers can only be requested up to 10 days in the future and will need to be approved.')}
              </Field.HelperText>
            </Field.Root>
            <Field.Root id="dofFile">
              <Field.Label>Upload DOF File</Field.Label>
              <input
                type="file"
                ref={inputRef}
                hidden
                data-testid="dof-file-input"
                onChange={handleChange}
              />
              <Box flexDir={"row"} display={'flex'} alignItems={'center'} w={'full'}>
                <Button
                  onClick={handleClick}
                  size="sm"
                  variant="outline"
                  border="1px solid white"
                  color="white"
                  px={4}
                >
                  Choose File
                </Button>
                {
                  file &&
                  <CloseButton
                    onClick={handleRemoveFile}
                    size="md"
                    data-testid="remove-file"
                    color={colors.grey100}
                  />
                }
              </Box>
              {errors?.dofFile &&
                <Field.HelperText color={colors.fpxRed}>
                  {errors.dofFile}
                </Field.HelperText>
              }

              {file && (
                <Box display={'flex'} flexDirection={'column'}>
                  <Box h={'24'} w={'24'}>
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover border border-gray-300 rounded" />
                  </Box>
                  <Text mt={2} color={colors.grey300}>{file.name}</Text>
                </Box>
              )}
            </Field.Root>
            <Field.Root id="notes" mt={8}>
              <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="notes">{t('Request Reason')}</Field.Label>
              <Textarea
                id="notes"
                placeholder={t('Provide a description of why you need this change')}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value)
                  setErrors((prev) => ({ ...prev, notes: null }))
                }}
                bgColor={colors.grey900}
                borderRadius='md'
                borderColor={errors?.notes ? colors.fpxRed : undefined}
                _focus={{
                  borderColor: errors?.notes ? colors.fpxRed : colors.grey100,
                  ringColor: errors?.notes ? colors.fpxRed : colors.grey100
                }}
              />
              {errors?.notes &&
                <Field.HelperText color={colors.fpxRed}>
                  {errors.notes}
                </Field.HelperText>
              }
            </Field.Root>
          </>
        }
      />
    </>
  )
}
